import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

/**
 * NearNow homepage — 100% contractor-facing (2026-02-04 · PM22 repositioning).
 *
 * Every homeowner-facing element (weather widget, town list, waitlist form,
 * scarcity rotator) has been removed to /find-a-pro. This page has one buyer:
 * the 45-65 y/o RI plumber / electrician / HVAC contractor who evaluates by
 * hearing, not by reading.
 */

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const INK  = "#1A1A2E";
const MUTED = "#5a5d66";
const BG = "#F8F9FA";

const HomePage = () => {
  const [guaranteeStats, setGuaranteeStats] = useState(null);

  useEffect(() => {
    api.get("/public/guarantee-stats").then(r => setGuaranteeStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" style={{ background: BG }}>
      {/* ================= HERO ================= */}
      <section
        id="hero"
        data-testid="hero-section"
        style={{
          background: NAVY,
          color: "#fff",
          padding: "72px 20px 60px",
          position: "relative",
          overflow: "hidden",
          borderBottom: `4px solid ${GOLD}`,
        }}
      >
        <img src="/nearnow-icon.png" alt="" aria-hidden="true"
             style={{ position: "absolute", right: -140, bottom: -140, width: 480, height: 480, opacity: 0.06, pointerEvents: "none" }} />
        <div style={{ maxWidth: 1080, margin: "0 auto", position: "relative" }}>
          <p style={{ color: GOLD, letterSpacing: 4, fontSize: 14, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
            NEARNOW · FOR RI CONTRACTORS
          </p>
          <h1
            className="font-display"
            data-testid="hero-headline"
            style={{ fontSize: 72, lineHeight: 1.02, margin: "12px 0 20px", color: "#fff", letterSpacing: 1 }}
          >
            YOU&apos;RE ON THE JOB.<br />
            <span style={{ color: GOLD }}>WE&apos;LL ANSWER THE PHONE.</span>
          </h1>
          <p style={{ fontSize: 21, color: "#cbd0db", maxWidth: 720, lineHeight: 1.5 }}>
            ARIA answers every call you miss — on the number you already have —
            takes down the job, and texts it to you in under 60 seconds.
          </p>
          <p style={{ fontSize: 17, color: "#8a92a8", maxWidth: 720, marginTop: 14 }}>
            No new number. No app to learn. No employee to hire.
          </p>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 28 }}>
            <a href="#hear-aria" data-testid="hero-hear-aria-cta"
               style={btnGold}
               onClick={(e) => { e.preventDefault(); document.getElementById("hear-aria")?.scrollIntoView({ behavior: "smooth" }); }}>
              📞 HEAR ARIA CALL YOU
            </a>
            <Link to="/onboard-simple" data-testid="hero-cta-onboard" style={btnGoldGhost}>
              START MY FREE MONTH →
            </Link>
          </div>
          <p style={{ fontSize: 14, color: "#8a92a8", marginTop: 14 }}>
            Free until your calls are forwarding. Cancel anytime.
          </p>

          <MiniHealthQuiz />
        </div>
      </section>

      {/* ================= HEAR ARIA DEMO — ARIA calls YOU ================= */}
      <section id="hear-aria" data-testid="hear-aria-section" style={{ background: "#fff", padding: "56px 20px", borderBottom: "1px solid #e5e7ee" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <p style={eyebrow}>THE DEMO · REVERSED</p>
          <h2 className="font-display" style={h2}>HEAR ARIA. FROM THE CUSTOMER SIDE.</h2>
          <p style={{ fontSize: 17, color: MUTED, maxWidth: 620, margin: "10px auto 22px", lineHeight: 1.55 }}>
            Enter your number below. ARIA will call you in the next 60 seconds and answer like it&apos;s your shop.
            Ask it about a water heater. See exactly what your customers will hear.
          </p>
          <DemoCallForm />
        </div>
      </section>

      {/* ================= GUARANTEE TRUST BAR (public credited $) ================= */}
      {guaranteeStats?.lifetime?.credited_usd > 0 && (
        <section data-testid="guarantee-trust-bar" style={{ background: GOLD, color: NAVY, padding: "22px 20px", textAlign: "center" }}>
          <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: 3, fontSize: 15, marginBottom: 4 }}>
            THE GUARANTEE, IN PUBLIC
          </div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            <span data-testid="guarantee-lifetime-usd">${guaranteeStats.lifetime.credited_usd.toLocaleString()}</span> refunded to <span data-testid="guarantee-lifetime-count">{guaranteeStats.lifetime.count}</span> contractor{guaranteeStats.lifetime.count === 1 ? "" : "s"} whose ARIA didn&apos;t hit {guaranteeStats.min_answered_threshold}+ missed-call catches in month one.
          </div>
        </section>
      )}

      {/* ================= COST OF DOING NOTHING ================= */}
      <section data-testid="missed-call-section" style={{ background: NAVY, color: "#fff", padding: "56px 20px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <p style={{ ...eyebrow, color: GOLD }}>THE COST OF DOING NOTHING</p>
          <h2 className="font-display" style={{ ...h2, color: "#fff" }}>WHAT&apos;S A MISSED CALL WORTH?</h2>

          <table style={{ width: "100%", marginTop: 20, borderCollapse: "collapse" }} data-testid="missed-call-table">
            <thead>
              <tr>
                <th style={thL}>JOB</th>
                <th style={thR}>AVERAGE TICKET</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={tdL}>Water heater replacement</td><td style={tdR}>$1,800</td></tr>
              <tr><td style={tdL}>Panel upgrade</td>              <td style={tdR}>$3,200</td></tr>
              <tr><td style={tdL}>Furnace / boiler replacement</td><td style={tdR}>$6,500</td></tr>
              <tr><td style={tdL}>Heat pump install</td>           <td style={tdR}>$12,000</td></tr>
            </tbody>
          </table>
          <p style={{ fontSize: 12, color: "#8a92a8", marginTop: 8 }}>
            Rhode Island market average, 2025-26.
          </p>

          <div style={{ marginTop: 26, padding: "20px 22px", background: "rgba(212,175,55,0.10)", border: `1px solid ${GOLD}`, borderRadius: 12 }}>
            <p style={{ fontSize: 20, color: "#fff", margin: 0, lineHeight: 1.5 }}>
              NearNow is <strong style={{ color: GOLD }}>$97 a month</strong>.<br />
              Miss one water heater a year and it cost you more than <strong>eighteen months</strong> of NearNow.
            </p>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how-it-works" data-testid="how-it-works-section" style={{ background: "#fff", padding: "64px 20px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <p style={eyebrow}>HOW IT WORKS</p>
          <h2 className="font-display" style={h2}>THREE STEPS. ABOUT FIVE MINUTES.</h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, marginTop: 28 }}>
            <Step n="1" title="We call you."
                  body="A real person walks you through forwarding your calls. Takes four minutes. You don't touch a computer." />
            <Step n="2" title="ARIA picks up when you can't."
                  body="Answers in your business name, asks the right questions for your trade, and gets the address and phone number." />
            <Step n="3" title="You get a text."
                  body="Name, number, address, and what they need — in under 60 seconds. Emergencies get flagged." />
          </div>

          <p style={{ textAlign: "center", marginTop: 30, fontSize: 18, color: MUTED }}>
            That&apos;s it. Your number stays your number. <strong style={{ color: NAVY }}>Nothing else changes.</strong>
          </p>
        </div>
      </section>

      {/* ================= DOLLARS-SAVED RECEIPT ================= */}
      <section data-testid="dollars-saved-section" style={{ background: BG, padding: "64px 20px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <p style={eyebrow}>THE MONDAY RECEIPT</p>
          <h2 className="font-display" style={h2}>EVERY MONDAY, WE SHOW YOU WHAT WE CAUGHT.</h2>

          <div data-testid="receipt-card" style={{
            marginTop: 26, background: "#fff", border: `2px solid ${GOLD}`, borderRadius: 14,
            padding: 28, boxShadow: "0 12px 30px rgba(10,22,40,0.08)"
          }}>
            <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2, color: GOLD, fontSize: 13 }}>
              NEARNOW · WEEKLY DOLLARS-SAVED RECEIPT · SAMPLE
            </div>
            <ul style={{ listStyle: "none", padding: 0, marginTop: 14, fontSize: 17, color: INK, lineHeight: 1.65 }}>
              <li>Last week ARIA answered <strong>11 calls</strong> you missed.</li>
              <li><strong>4</strong> were job requests.</li>
              <li><strong>1</strong> was an emergency, escalated to your cell in 90 seconds.</li>
              <li>Estimated value of work captured: <strong style={{ color: "#1A7A4A" }}>$4,200</strong></li>
              <li>Your cost last week: <strong>$22.40</strong></li>
            </ul>
            <p style={{ fontSize: 13, color: MUTED, marginTop: 14, fontStyle: "italic" }}>
              No dashboard to log into. It comes to your phone.
            </p>
          </div>
        </div>
      </section>

      {/* ================= WALL OF WINS (rotating anonymized receipts) ================= */}
      <WallOfWins />

      {/* ================= WHAT NEARNOW IS NOT ================= */}
      <section data-testid="objection-section" style={{ background: "#fff", padding: "64px 20px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <p style={eyebrow}>OBJECTIONS, HANDLED</p>
          <h2 className="font-display" style={h2}>WHAT NEARNOW IS NOT.</h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18, marginTop: 24 }}>
            <ObjBlock title="It's not another employee."     body="No salary, no payroll taxes, no training, no calling out sick." />
            <ObjBlock title="It's not a shared lead."         body="Angi sells the same call to five contractors. Every call ARIA answers is yours and only yours." />
            <ObjBlock title="It's not a CRM."                 body="There's nothing to learn. It texts you. You call the customer back. That's the whole thing." />
            <ObjBlock title="It's not a new phone number."    body="Your number stays your number. Your customers never know the difference." />
          </div>
        </div>
      </section>

      {/* ================= PRICING PREVIEW + GUARANTEE ================= */}
      <section data-testid="pricing-preview" style={{ background: NAVY, color: "#fff", padding: "64px 20px", textAlign: "center" }}>
        <p style={{ ...eyebrow, color: GOLD }}>PRICING</p>
        <h2 className="font-display" style={{ ...h2, color: "#fff", fontSize: 60 }}>
          $97 A MONTH. FIRST MONTH FREE.
        </h2>
        <p style={{ fontSize: 18, color: "#cbd0db", maxWidth: 720, margin: "16px auto 0", lineHeight: 1.55 }}>
          Your free month doesn&apos;t start until your calls are forwarding — so you never pay for a month you didn&apos;t use.
        </p>
        <p style={{ fontSize: 18, color: GOLD, maxWidth: 780, margin: "12px auto 0", lineHeight: 1.55 }}>
          If ARIA doesn&apos;t answer at least 10 calls you would have missed in your first 30 days, month two is on us.
        </p>
        <div style={{ marginTop: 26, display: "flex", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
          <Link to="/onboard-simple" data-testid="home-start-cta" style={btnGold}>
            START MY FREE MONTH
          </Link>
          <Link to="/pricing" data-testid="home-see-plans" style={btnGoldGhost}>
            SEE ALL PLANS
          </Link>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section data-testid="faq-section" style={{ background: "#fff", padding: "64px 20px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <p style={eyebrow}>FAQ</p>
          <h2 className="font-display" style={h2}>QUESTIONS CONTRACTORS ASK.</h2>

          <div style={{ marginTop: 24 }}>
            <Faq q="Does my phone number change?" a="No. Your number stays exactly the same." />
            <Faq q="What if I want to answer the call myself?" a="Answer it. ARIA only picks up when you don't." />
            <Faq q="Does it sound like a robot?" a='Try it — scroll up to "HEAR ARIA CALL YOU," drop your number, and see for yourself.' />
            <Faq q="What happens after hours?" a="ARIA works 24/7. Emergencies get escalated to your cell." />
            <Faq q="Do I have to learn any software?" a="No. Everything comes to you as a text." />
            <Faq q="Can I cancel?" a="Any time, from the link in your welcome email. No call required." />
            <Faq q="Do you sell my leads to other contractors?" a="Never. One lead, one pro." />
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section data-testid="final-cta" style={{ background: `linear-gradient(135deg, ${GOLD} 0%, #b89530 100%)`, color: NAVY, padding: "64px 20px", textAlign: "center" }}>
        <h2 className="font-display" style={{ fontSize: 60, margin: 0, letterSpacing: 1, lineHeight: 1.02 }}>
          READY TO STOP MISSING WORK?
        </h2>
        <p style={{ fontSize: 20, maxWidth: 700, margin: "16px auto 0", fontWeight: 500 }}>
          Add your business. Free until your calls forward. Cancel anytime.
        </p>
        <Link to="/onboard-simple" data-testid="final-cta-btn" style={{ textDecoration: "none" }}>
          <button className="btn-navy tap" style={{ marginTop: 28, fontSize: 26, padding: "20px 36px" }}>
            START MY FREE MONTH →
          </button>
        </Link>
      </section>
    </div>
  );
};

// --- small components ---
const WallOfWins = () => {
  const [wins, setWins] = useState([]);
  const [idx, setIdx] = useState(0);
  const [fallback, setFallback] = useState(false);
  const [hasReal, setHasReal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/wall-of-wins?limit=12&days=60").then((r) => {
      if (cancelled) return;
      setWins(r.data.wins || []);
      setFallback(!!r.data.fallback_used);
      setHasReal(!!r.data.has_real_testimonials);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (wins.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % wins.length), 5000);
    return () => clearInterval(t);
  }, [wins]);

  if (wins.length === 0) return null;
  const current = wins[idx];
  const isReal = !!current.real;

  return (
    <section id="wall-of-wins" data-testid="wall-of-wins" style={{ background: NAVY, padding: "48px 20px", borderTop: "1px solid #1a2640", borderBottom: `2px solid ${GOLD}` }}>
      <div style={{ maxWidth: 920, margin: "0 auto", textAlign: "center" }}>
        <div style={{ color: GOLD, letterSpacing: 4, fontSize: 12, fontFamily: "Bebas Neue, sans-serif", marginBottom: 6 }}>
          {hasReal ? "REAL CUSTOMER WINS · LIVE" : "THE WALL OF WINS · LIVE"}
        </div>
        <h2 className="font-display" style={{ ...h2, color: "#fff", fontSize: 42 }}>
          {hasReal ? "WHAT RI HOMEOWNERS SAY ABOUT NEARNOW PARTNERS." : "THIS WEEK ARIA CAUGHT WORK ACROSS RI."}
        </h2>
        <div key={current.id} data-testid={`win-${idx}`} style={{
          marginTop: 22, background: "rgba(212,175,55,0.10)", border: `1px solid ${GOLD}`,
          borderRadius: 14, padding: "26px 22px", maxWidth: 720, marginLeft: "auto", marginRight: "auto",
        }}>
          {isReal && current.stars && (
            <div style={{ color: GOLD, fontSize: 18, letterSpacing: 3, marginBottom: 8 }}>
              {"★".repeat(current.stars)}{"☆".repeat(5 - current.stars)}
            </div>
          )}
          <div style={{ color: "#fff", fontSize: 22, lineHeight: 1.4, fontStyle: isReal ? "italic" : "normal" }}>
            {isReal ? `"${current.quote}"` : current.text}
          </div>
          {isReal && (
            <div style={{ color: "#cbd0db", fontSize: 14, marginTop: 12 }}>
              — {current.customer_name || "Verified customer"} · <strong style={{ color: "#fff" }}>{current.business_name}</strong> · {current.city}
            </div>
          )}
          {fallback && !hasReal && (
            <div style={{ fontSize: 11, color: "#8a92a8", marginTop: 12, letterSpacing: 1 }}>
              SAMPLE · REAL WINS APPEAR HERE AS ARIA CAPTURES THEM
            </div>
          )}
        </div>
        {wins.length > 1 && (
          <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 6 }}>
            {wins.map((_, i) => (
              <span key={i} onClick={() => setIdx(i)} data-testid={`win-dot-${i}`}
                    style={{ width: i === idx ? 22 : 8, height: 8, borderRadius: 999,
                              background: i === idx ? GOLD : "#3a4258", transition: "width .3s", cursor: "pointer" }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * 20-second homepage quiz — 1 question, 4 options, instant score.
 *
 * Purpose: warm up the casual visitor before they hit /pricing.
 * Each answer maps to a bleed severity (0-3); we compute an implied
 * monthly $-loss and route them to /pricing with a query param the
 * pricing page can read to pre-select the recommended tier.
 */
const QUIZ_OPTIONS = [
  { key: "answering",   label: "I answer my own phone",
    body: "Every call while I'm on the truck goes to voicemail — or Google.",
    loss_lo: 1800, loss_hi: 4200, tier: "growth",
    recommendation: "ARIA · $97" },
  { key: "quotes",      label: "Quotes going cold",
    body: "I send estimates, then they ghost me.",
    loss_lo: 4500, loss_hi: 9500, tier: "aria_pro",
    recommendation: "ARIA Pro · $197" },
  { key: "office",      label: "Thinking of hiring an office person",
    body: "The paperwork and callbacks are eating my nights.",
    loss_lo: 8000, loss_hi: 24000, tier: "front_office",
    recommendation: "Front Office · $497" },
  { key: "reviews",     label: "Not enough Google reviews",
    body: "Great work, no proof. Competition looks bigger than they are.",
    loss_lo: 2400, loss_hi: 6800, tier: "aria_pro",
    recommendation: "ARIA Pro · $197" },
];

const MiniHealthQuiz = () => {
  const [picked, setPicked] = useState(null);

  const submit = (opt) => setPicked(opt);
  const loss = picked ? Math.round((picked.loss_lo + picked.loss_hi) / 2) : 0;

  return (
    <div data-testid="mini-health-quiz" style={{
      marginTop: 34, background: "rgba(212,175,55,0.08)",
      border: "1px solid rgba(212,175,55,0.35)", borderRadius: 14, padding: 22,
    }}>
      {!picked ? (
        <>
          <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: 3, color: GOLD, fontSize: 13, marginBottom: 6 }}>
            20-SECOND HEALTH SCORE
          </div>
          <div style={{ fontSize: 20, color: "#fff", marginBottom: 14, fontWeight: 600 }}>
            Which one bleeds you the most today?
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            {QUIZ_OPTIONS.map((o) => (
              <button key={o.key} onClick={() => submit(o)}
                      data-testid={`quiz-option-${o.key}`}
                      style={{
                        background: "#0e1a2e", color: "#fff",
                        border: "1px solid #2a3852", borderRadius: 10,
                        padding: "14px 16px", textAlign: "left", cursor: "pointer",
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = "#132038"; }}
                      onMouseOut={(e) => { e.currentTarget.style.borderColor = "#2a3852"; e.currentTarget.style.background = "#0e1a2e"; }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{o.label}</div>
                <div style={{ fontSize: 13, color: "#8a92a8" }}>{o.body}</div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div data-testid="quiz-result">
          <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: 3, color: GOLD, fontSize: 13, marginBottom: 6 }}>
            YOUR RESULT
          </div>
          <div style={{ fontSize: 22, color: "#fff", lineHeight: 1.4, marginBottom: 10 }}>
            You&apos;re bleeding about <strong data-testid="quiz-loss" style={{ color: GOLD }}>${loss.toLocaleString()}/mo</strong> in captured work.
          </div>
          <div style={{ fontSize: 15, color: "#cbd0db", marginBottom: 16 }}>
            The plan built for your bleed: <strong data-testid="quiz-recommendation" style={{ color: "#fff" }}>{picked.recommendation}</strong>.
            Free until your calls are forwarding.
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to={`/pricing?warm=${picked.key}&highlight=${picked.tier}`}
                  data-testid="quiz-cta-pricing"
                  style={{ background: GOLD, color: NAVY, textDecoration: "none", padding: "14px 22px", borderRadius: 10, fontWeight: 800, letterSpacing: 2, fontSize: 15, fontFamily: "Bebas Neue, sans-serif" }}>
              SEE {picked.recommendation.split(" · ")[0].toUpperCase()} →
            </Link>
            <button onClick={() => setPicked(null)}
                    data-testid="quiz-restart"
                    style={{ background: "transparent", color: "#8a92a8", border: "1px solid #2a3852", padding: "14px 22px", borderRadius: 10, fontSize: 14, cursor: "pointer" }}>
              Pick another
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DemoCallForm = () => {
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!phone) return;
    setStatus("sending"); setMessage("");
    try {
      const r = await api.post("/public/aria-demo-request", {
        phone, first_name: firstName,
      }).then((x) => x.data);
      if (r.ok) {
        setStatus("ok");
        setMessage(r.message || "ARIA is dialing you now.");
      } else {
        setStatus("error");
        setMessage(r.message || r.error || "Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || err?.message || "Something went wrong.");
    }
  };

  return (
    <form onSubmit={submit} data-testid="aria-demo-form" style={{ background: "#F8F9FA", border: "2px solid #D4AF37", borderRadius: 14, padding: 22 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <input
          type="text"
          placeholder="Your first name (optional)"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          data-testid="aria-demo-name-input"
          style={{ padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 16, outline: "none" }}
        />
        <input
          type="tel"
          placeholder="Your phone number — (401) 555-1234"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="aria-demo-phone-input"
          required
          style={{ padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 16, outline: "none" }}
        />
        <button
          type="submit"
          disabled={status === "sending" || !phone}
          data-testid="aria-demo-submit"
          style={{ background: "#D4AF37", color: "#0A1628", border: "none", padding: "16px 22px", borderRadius: 12, fontWeight: 800, letterSpacing: 2, fontSize: 17, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: status === "sending" ? "wait" : "pointer" }}
        >
          {status === "sending" ? "DIALING…" : status === "ok" ? "✓ ARIA IS CALLING YOU" : "📞 CALL ME NOW"}
        </button>
        {message && (
          <div
            data-testid={status === "ok" ? "aria-demo-ok" : "aria-demo-msg"}
            style={{
              padding: "10px 14px", borderRadius: 10, fontSize: 14, textAlign: "left",
              background: status === "ok" ? "#E7F5EE" : "#FDECEC",
              color: status === "ok" ? "#0F5132" : "#A32D2D",
            }}
          >
            {message}
          </div>
        )}
        <div style={{ fontSize: 12, color: "#8a92a8", marginTop: 4, textAlign: "center" }}>
          One-time demo call. We don&apos;t sell your number. Answer it like a real customer.
        </div>
      </div>
    </form>
  );
};

const Step = ({ n, title, body }) => (
  <div data-testid={`step-${n}`} style={{ background: BG, border: "1px solid #e5e7ee", borderRadius: 14, padding: 22 }}>
    <div style={{ fontFamily: "Bebas Neue, sans-serif", color: GOLD, fontSize: 44, letterSpacing: 1 }}>{n}</div>
    <div className="font-display" style={{ fontSize: 24, color: NAVY, letterSpacing: 1, marginBottom: 6 }}>{title.toUpperCase()}</div>
    <div style={{ fontSize: 16, color: INK, lineHeight: 1.55 }}>{body}</div>
  </div>
);
const ObjBlock = ({ title, body }) => (
  <div style={{ background: BG, borderLeft: `4px solid ${GOLD}`, padding: "16px 18px", borderRadius: 8 }}>
    <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1, fontSize: 22, color: NAVY, marginBottom: 6 }}>{title.toUpperCase()}</div>
    <div style={{ fontSize: 16, color: INK, lineHeight: 1.55 }}>{body}</div>
  </div>
);
const Faq = ({ q, a }) => (
  <details data-testid="faq-item" style={{ borderBottom: "1px solid #e5e7ee", padding: "16px 0" }}>
    <summary style={{ cursor: "pointer", fontSize: 18, color: NAVY, fontWeight: 700, listStyle: "none" }}>
      {q}
    </summary>
    <div style={{ marginTop: 8, fontSize: 16, color: INK, lineHeight: 1.55 }}>{a}</div>
  </details>
);

const eyebrow = { color: GOLD, fontSize: 14, letterSpacing: 4, fontFamily: "Bebas Neue, sans-serif", margin: 0 };
const h2 = { fontSize: 46, letterSpacing: 1, color: NAVY, margin: "4px 0 10px", lineHeight: 1.05 };
const btnGold = { display: "inline-block", background: GOLD, color: NAVY, textDecoration: "none", padding: "18px 28px", borderRadius: 12, fontWeight: 800, letterSpacing: 2, fontSize: 18, fontFamily: "Bebas Neue, sans-serif", cursor: "pointer", border: "none" };
const btnGoldGhost = { display: "inline-block", background: "transparent", color: GOLD, border: `1.5px solid ${GOLD}`, textDecoration: "none", padding: "18px 28px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 18, fontFamily: "Bebas Neue, sans-serif" };
const thL = { textAlign: "left",  color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2, fontSize: 14, padding: "10px 0", borderBottom: `2px solid ${GOLD}` };
const thR = { textAlign: "right", color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2, fontSize: 14, padding: "10px 0", borderBottom: `2px solid ${GOLD}` };
const tdL = { textAlign: "left",  color: "#fff", fontSize: 18, padding: "14px 0", borderBottom: "1px solid #1a2640" };
const tdR = { textAlign: "right", color: "#fff", fontSize: 22, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1, padding: "14px 0", borderBottom: "1px solid #1a2640" };

export default HomePage;
